<script>
    import FILTER_TYPES from '@/components/query/filter-types';
    import _ from '@/common/helpers';
    import {DateTimeFormats} from '@/common/datetime-formats';
    import format from '@/common/format';
    import {vIcon} from '@/components/form';
    import ConditionOperators from '@/components/query/spec/condition-operators';
    import {getSpecialResourceFieldPath} from '@/common/api/definitions/special/special-fields';

    export default {
        name: 'filter-summary',
        props: {
            /**
             * @type CriteriaCondition
             */
            condition: Object,
            resourceField: {
                type: [Object, Function],
                default: () => ({}),
            },
            isCriteria: {
                type: Boolean,
                default: false,
            },
            dividerIcon: {
                type: String,
                default: 'caret-right',
            },
        },
        computed: {
            isTimePicker() {
                return this.resourceField && this.resourceField.options && this.resourceField.options.options.timePicker;
            },
        },
        /**
         * Renders the filter summary recursively.
         * @param {Function} createElement
         */
        render(createElement) {
            try {
                const elements = [].concat(this.expandRecursively({
                    condition: this.condition,
                    createElement,
                }));
                return createElement('span', {class: 'filter-summary'}, elements);
            } catch (error) {
                // do nothing
            }
            // if there was an error parsing the condition
            // then we render a fallback element to prevent UI
            // errors
            return this.getFallbackElement(createElement);
        },
        methods: {
            /**
             * Returns the rendered node of the fallback element
             * for when the condition parsing fails.
             * @param {Function} createElement
             */
            getFallbackElement(createElement) {
                // temporary fallback
                return createElement('div', {title: 'Error reading condition'}, '...');
            },
            /**
             * Generate a summary from a condition regardless of recursive depth.
             * Returns an array of nodes to be rendered.
             * @param {CriteriaCondition} condition
             * @param {Function} createElement
             * @param {Object} extraAttributes?
             * @returns {array}
             */
            expandRecursively({condition, createElement, extraAttributes = {class: 'particle'}}) {
                // if the condition is composed of other
                // conditions then recursively expand it
                if (this.isCriteria && condition.isComposed()) {
                    if (condition.isNegative() || condition.isContains()) {
                        return this.expandRecursively({
                            condition: condition.condition,
                            createElement,
                            extraAttributes,
                        });
                    }

                    return this.separateChildren({
                        values: condition.conditions
                            .map(v => this.expandRecursively({
                                condition: v,
                                createElement,
                                extraAttributes: {class: 'particle inline-s'},
                            })),
                        delimiter: this.getOperatorSeparator(createElement, condition.op),
                    });
                }
                // otherwise render it's final summary
                return [createElement(
                    'span',
                    extraAttributes,
                    this.getSummary({condition, createElement}),
                )];
            },
            /**
             * Generate a separator element usually used within a composed
             * condition. e.g. `or`, `and`.
             * @param {Function} createElement
             * @param {string} operator
             */
            getOperatorSeparator(createElement, operator) {
                return createElement('strong', {class: 'condition-separator inline-s'}, operator);
            },
            /**
             * Separate a list of nodes or values with a delimiter
             * element by injecting it between each pair of two values.
             * @param {Array} values
             * @param {Object|string} delimiter
             */
            separateChildren({values, delimiter}) {
                return _.separateArray(values, delimiter);
            },
            /**
             * Check whether the condition is newly added and contains no data.
             * @param {CriteriaCondition} condition
             */
            isNewCondition(condition) {
                return condition.op === null
                    && [
                        condition.value,
                        condition.values,
                        condition.listId,
                        condition.min,
                        condition.max,
                        condition.comparePath,
                    ].find(v => v !== null) === undefined;
            },
            /**
             * Generate the nodes contained within a single-level condition.
             * @param {CriteriaCondition} condition
             * @param {Function} createElement
             * @returns {array}
             */
            getSummary({condition, createElement}) {
                /**
                 * Resolve the field definition from the provided delegate
                 * or object.
                 * @param {Function|Object} field
                 * @returns {{}}
                 */
                const field = typeof this.resourceField === typeof Function
                    ? this.resourceField(condition)
                    : this.resourceField;
                /**
                 * Split the text path into an array and process it with `unCamelCase`
                 * @param {string} path
                 * @returns {array}
                 */
                const pathToArray = path => path.slice(1)
                    .split('/')
                    .map(_.unCamelCase);
                /**
                 * Resolve the label of the current condition
                 * @param {string|null} override
                 * @returns {array}
                 */
                const resolveLabel = (override = null) => {
                    if (this.isCriteria) {
                        if (condition.isSpecialCondition()) {
                            // if the condition is custom defined then don't
                            // attempt to resolve the path as it is missing,
                            // instead use the special method
                            override = getSpecialResourceFieldPath(condition);
                        }
                        return pathToArray(override || condition.path);
                    }
                    return [field.label];
                };
                /**
                 * Generate a label element for the condition. Optionally
                 * use the override provided and resolve its value.
                 * @param {string?} override
                 */
                const generateLabelElement = override => createElement(
                    'span',
                    {class: 'filter-label inline-xs'},
                    this.separateChildren({
                        values: resolveLabel(override),
                        delimiter: createElement(vIcon, {props: {icon: this.dividerIcon}}),
                    }),
                );
                /**
                 * Wrap text nodes with a span element to enable spacing between sets of words
                 * @params {Array} nodes
                 * @returns {array}
                 */
                const wrapTextNodes = nodes => nodes.map(n => createElement('span', {class: 'inline-xs'}, n));
                // starting point of th e summary
                const summary = [
                    generateLabelElement(),
                ];
                /**
                 * Generate a basic summary from the text nodes provided.
                 * @param {Array} nodes
                 * @returns {array}
                 */
                const generateBasicSummary = nodes => summary.concat(wrapTextNodes(nodes));

                // if we have a new condition, display only the path summary
                if (this.isNewCondition(condition)) {
                    return summary;
                }

                // start the summary building flow
                if ([
                    FILTER_TYPES.text,
                    FILTER_TYPES.enum,
                    FILTER_TYPES.select,
                ].includes(field.type)) {
                    const listSummary = () => {
                        const concurrentDisplayCount = (field.options && field.options.count) || 2;
                        const valuesCount = condition.values.length;
                        if (valuesCount === 0) {
                            return [];
                        }
                        const displayedValues = [...condition.values].splice(0, concurrentDisplayCount);
                        const suffix = valuesCount > concurrentDisplayCount ? ` and ${valuesCount - concurrentDisplayCount} more` : '';
                        return suffix ? [
                            displayedValues.join(', '),
                            suffix,
                        ] : [
                            displayedValues.join(', '),
                        ];
                    };
                    if (this.isCriteria) {
                        if (condition.isUnknown()) {
                            return generateBasicSummary([
                                condition.isNotUnknown() ? 'is not' : 'is',
                                'empty',
                            ]);
                        }
                        if (condition.isComparison()) {
                            return summary.concat([
                                createElement(
                                    'strong',
                                    {class: 'inline-xs'},
                                    condition.isParentNegative ? 'not same as' : 'same as',
                                ),
                                generateLabelElement(condition.comparePath),
                            ]);
                        }
                        if (condition.isValueList()) {
                            return generateBasicSummary([
                                condition.isParentNegative ? 'is not' : 'is',
                                // TODO fetch lists and use name instead of ID
                                ` in list ${_.truncate(condition.listId, {dots: true})}`,
                            ]);
                        }
                        if (condition.op === ConditionOperators.equals) {
                            // custom field enum with restrictions
                            return generateBasicSummary([
                                'equals',
                                condition.value,
                            ]);
                        }
                        if (condition.isParentContains) {
                            // special case for `contains`
                            return summary.concat([
                                wrapTextNodes([
                                    condition.isParentNegative ? 'do not contain' : 'contain',
                                ]),
                                createElement('em', {class: 'list-values'}, listSummary()),
                            ]);
                        }
                        // default case: `in` values
                        return summary.concat([
                            wrapTextNodes([
                                condition.isParentNegative ? 'is not' : 'is',
                                'in',
                            ]),
                            createElement('em', {class: 'list-values'}, listSummary()),
                        ]);
                    }
                    return summary.concat([
                        wrapTextNodes([
                            'is in',
                        ]),
                        createElement('em', {class: 'list-values'}, listSummary()),
                    ]);
                }
                if (field.type === FILTER_TYPES.numeric
                    || field.type === FILTER_TYPES.datetime) {
                    // TODO handle custom field type `date`
                    const isRange = this.isCriteria
                        ? condition.op === ConditionOperators.between
                        : condition.min !== null && condition.max !== null;
                    const isLTE = this.isCriteria
                        ? condition.op === ConditionOperators.lte
                        : condition.max !== null;
                    /* eslint-disable no-nested-ternary */
                    const dateFormat = this.isTimePicker
                        ? DateTimeFormats.datetime
                        : DateTimeFormats.date;
                    let formatter = field.type === FILTER_TYPES.datetime
                        ? (this.isCriteria ? format.dateTime : value => format.dateUTC(value, dateFormat))
                        : format.number;
                    if (isRange) {
                        // special case for custom fields of type `date`
                        if (/^\w{4}-\w{2}-\w{2}$/.test(condition.min)) {
                            // don't parse the time and keep the time zone
                            formatter = format.dateLocal;
                        }
                        return generateBasicSummary([
                            'is between',
                            formatter(condition.min),
                            'and',
                            formatter(condition.max),
                        ]);
                    }
                    if (isLTE) {
                        return generateBasicSummary([
                            'is lower than',
                            'or',
                            'equal to',
                            formatter(this.isCriteria ? condition.value : condition.max),
                        ]);
                    }
                    return generateBasicSummary([
                        'is greater than',
                        'or',
                        'equal to',
                        formatter(this.isCriteria ? condition.value : condition.min),
                    ]);
                }
                if (field.type === FILTER_TYPES.boolean) {
                    return generateBasicSummary([
                        'is',
                        String(condition.value),
                    ]);
                }
                if (field.type === FILTER_TYPES.random) {
                    return generateBasicSummary([
                        'is',
                        `${condition.value}%`,
                    ]);
                }
                // fallback
                return this.getFallbackElement(createElement);
            },
        },
    };
</script>
